import { motion } from "framer-motion";
import { MapPin, Clock, CalendarHeart } from "lucide-react";
import { WEDDING_EVENT, COUPLE } from "@/data/wedding";
import { FloatingInstrument, GoldDivider } from "./Ornaments";
import { WEDDING } from "@/constants/testIds";

const LEAF =
  "https://images.unsplash.com/photo-1528207620916-db0d5aab17cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxiYW5hbmElMjBsZWFmJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODY5ODI0Mzl8MA&ixlib=rb-4.1.0&q=85";

const rise = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const SadhyaInvite = () => {
  return (
    <section
      id="invitation"
      className="relative overflow-hidden bg-[var(--kasavu)] px-6 py-28 md:py-36"
    >
      {/* chapter marker */}
      <motion.div
        variants={rise}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="mx-auto mb-14 max-w-3xl text-center"
      >
        <p className="font-body text-[11px] uppercase tracking-mega text-[var(--gold-deep)]">
          Chapter I —{" "}
          <span className="font-malayalam normal-case tracking-normal">
            {WEDDING_EVENT.malayalam}
          </span>
        </p>
        <h2 className="mt-4 font-display text-5xl italic text-[var(--maroon)] md:text-7xl">
          {WEDDING_EVENT.title}
        </h2>
        <p className="mx-auto mt-4 max-w-md font-body text-sm leading-relaxed text-[var(--muted)]">
          Shared with heartfelt warmth.
        </p>
      </motion.div>

      {/* Banana leaf */}
      <motion.div
        variants={rise}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="relative mx-auto max-w-4xl"
      >
        <FloatingInstrument
          src="/assets/nadaswaram.jpeg"
          label="Nadaswaram"
          size={104}
          className="-left-4 -top-8 z-20 md:-left-12"
          rotate={-8}
        />
        <FloatingInstrument
          src="/assets/thavil.jpeg"
          label="Thavil"
          size={104}
          className="-bottom-8 -right-4 z-20 md:-right-12"
          delay={0.4}
          rotate={8}
        />

        <div
          className="leaf-shape relative overflow-hidden p-8 shadow-[0_40px_90px_-30px_rgba(47,82,39,0.7)] md:p-14"
          style={{
            backgroundImage: `linear-gradient(rgba(18,40,12,0.14), rgba(18,40,12,0.28)), url(${LEAF})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* central vein */}
          <div className="pointer-events-none absolute inset-y-6 left-1/2 w-px -translate-x-1/2 bg-white/25" />

          {/* Invitation text laid directly on the leaf (sadhya-style) */}
          <div className="relative rounded-[26px] px-6 py-10 text-center md:px-14 md:py-14">
            {/* soft scrim only behind the text for legibility, leaf stays visible */}
            <div className="pointer-events-none absolute inset-0 rounded-[26px] bg-[radial-gradient(ellipse_at_center,rgba(12,30,8,0.62),rgba(12,30,8,0.12)_75%,transparent)]" />
            <div className="pointer-events-none absolute inset-0 rounded-[26px] ring-1 ring-inset ring-gold/40" />

            <div className="relative z-10">
              <p className="font-script text-4xl text-[#F7E6A6] [text-shadow:0_2px_12px_rgba(0,0,0,0.65)] md:text-5xl">
                You are invited
              </p>

              <p className="mx-auto mt-5 max-w-xl font-body text-[13px] leading-relaxed text-white/90 [text-shadow:0_1px_8px_rgba(0,0,0,0.75)] md:text-sm">
                {COUPLE.invitation}
              </p>

              <p className="mt-6 font-display text-3xl italic text-white [text-shadow:0_2px_12px_rgba(0,0,0,0.75)] md:text-4xl">
                {COUPLE.bride.name}{" "}
                <span className="font-script text-[#F7E6A6]">&amp;</span>{" "}
                {COUPLE.groom.name}
              </p>

              <GoldDivider className="my-7" />

              <div className="grid gap-8 md:grid-cols-3">
                <Detail icon={CalendarHeart} label="Date">
                  {WEDDING_EVENT.date}
                </Detail>
                <Detail icon={Clock} label={WEDDING_EVENT.ritual}>
                  {WEDDING_EVENT.time}
                </Detail>
                <Detail icon={MapPin} label="Venue">
                  {WEDDING_EVENT.venue}
                </Detail>
              </div>

              <p className="mx-auto mt-8 max-w-md font-body text-sm text-white/90 [text-shadow:0_1px_8px_rgba(0,0,0,0.7)]">
                {WEDDING_EVENT.address}
              </p>

              <a
                data-testid={WEDDING.mapLinkWedding}
                href={WEDDING_EVENT.mapUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-[var(--gold)] px-6 py-3 font-body text-[11px] uppercase tracking-[0.2em] text-[var(--green-deep)] shadow-lg transition-transform duration-300 hover:scale-105"
              >
                <MapPin className="h-4 w-4" strokeWidth={1.8} />
                Open location
              </a>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

const Detail = ({ icon: Icon, label, children }) => (
  <div className="flex flex-col items-center">
    <Icon className="h-6 w-6 text-[#F2DE8F] [filter:drop-shadow(0_1px_4px_rgba(0,0,0,0.6))]" strokeWidth={1.6} />
    <p className="mt-3 font-body text-[10px] uppercase tracking-[0.28em] text-[#F2DE8F] [text-shadow:0_1px_6px_rgba(0,0,0,0.7)]">
      {label}
    </p>
    <p className="mt-2 font-display text-xl italic leading-snug text-white [text-shadow:0_2px_10px_rgba(0,0,0,0.7)] md:text-2xl">
      {children}
    </p>
  </div>
);
